import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  useForm,
  RegisterOptions,
  Path,
  DefaultValues,
  SubmitHandler,
  FieldValues,
} from "react-hook-form";
import Button from "./Button";
import Input from "./InputField";
import { compressImage } from "../utils/imageCompression";

export type FieldType =
  | "text"
  | "number"
  | "email"
  | "tel"
  | "date"
  | "textarea"
  | "chips"
  | "info"
  | "file";

// --- New: support two kinds of items in the fields array ---
// Section header item
export type SectionConfig = {
  sectionTitle: string;
  // you can add styling hints later, e.g. colSpan?: number;
};

// Input field item (same as before)
export type InputFieldConfig = {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  accept?: string;
  pattern?: string;
  maxLength?: number;
  minLength?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  defaultValue?: string;
  readOnly?: boolean;
  prefix?: string;
  // used for informational fields (display-only)
  infoText?: string;
   previewUrlField?: string; 
  conditional?: {
    field: string;
    value: string;
  };
};

// union type used by the component
export type FieldConfig = SectionConfig | InputFieldConfig;

interface FormBuilderProps<T extends FieldValues> {
  fields: FieldConfig[];
  onSubmit: (values: T) => void;
  onCancel?: () => void;
  submitLabel?: string;
  error?: Record<string, string[]> | null;
  defaultValues?: DefaultValues<T>; // ✅ correct type for react-hook-form
}

const FormBuilder = forwardRef(function FormBuilder<T extends FieldValues>(
  {
    fields,
    onSubmit,
    onCancel,
    submitLabel = "Save",
    error,
    defaultValues,
  }: FormBuilderProps<T>,
  ref: React.Ref<unknown> | undefined
) {
  // use DefaultValues<T> instead of Partial<T>
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
     clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<T>({
    mode: "onChange",
    defaultValues, // fully type-safe now
    
  });

  const [previews, setPreviews] = useState<Record<string, string[]>>({});
const currentDefaults = defaultValues;

useImperativeHandle(ref, () => ({
  reset,
  clearErrors,
  clearPreviews: () => {
    Object.values(previews).forEach((urls) => urls.forEach((u) => URL.revokeObjectURL(u)));
    setPreviews({});
  },
}));


  // ✅ reset when editing (skip section entries)
useEffect(() => {
  if (defaultValues) {
    reset(defaultValues);
  }
}, [defaultValues, reset]);

  // ✅ correct type for react-hook-form submit handler
const handleFormSubmit: SubmitHandler<T> = async (values) => {
  await onSubmit(values);
};


  const handleChipClick = (fieldName: string, option: string, readOnly?: boolean) => {
    if (readOnly) return;
    setValue(fieldName as any, option as any, { shouldDirty: true, shouldTouch: true });

  };

  const handleFileChange = (fieldName: string, files?: FileList | null, readOnly?: boolean) => {
    if (readOnly) return;

    if (!files || files.length === 0) {
      setValue(fieldName as any, null as any, { shouldDirty: true, shouldTouch: true });
      setPreviews((s) => ({ ...s, [fieldName]: [] }));
      return;
    }

    const file = files[0]; // SINGLE FILE

    // Compress image if it's an image type
    const compressAndSetFile = async () => {
      let fileToSet = file;
      
      if (file.type.startsWith("image/")) {
        try {
          // Compress with default settings: max 1200x1200, 80% quality
          fileToSet = await compressImage(file, 1200, 1200, 0.8);
        } catch (err) {
          console.error("Image compression failed, using original:", err);
          // Fall back to original file if compression fails
        }
      }

      setValue(fieldName as any, fileToSet as any, {
        shouldDirty: true,
        shouldTouch: true,
      });

      const url = URL.createObjectURL(fileToSet);

      // cleanup old
      if (previews[fieldName]) {
        previews[fieldName].forEach((u) => URL.revokeObjectURL(u));
      }

      setPreviews((s) => ({ ...s, [fieldName]: [url] }));
    };

    compressAndSetFile();
  };

  // FilePreview now expects an InputFieldConfig (so sections don't get passed)
const FilePreview: React.FC<{ field: InputFieldConfig }> = ({ field }) => {
  const urls = previews[field.name] || [];
  const fileValue = watch(field.name as any) as unknown;

  const hasNewFile =
    typeof fileValue === "object" &&
    fileValue !== null &&
    fileValue instanceof File;

  const existingUrl =
    field.previewUrlField && defaultValues
      ? (defaultValues as any)[field.previewUrlField]
      : null;

  return (
    <div className="flex gap-3 items-center">
      <div className="flex gap-2">
        {hasNewFile && urls.length > 0 && (
          <div className="w-16 h-16 rounded-md overflow-hidden border">
            <img src={urls[0]} className="w-full h-full object-cover" />
          </div>
        )}

        {!hasNewFile && existingUrl && (
          <div className="w-16 h-16 rounded-md overflow-hidden border">
            <img src={existingUrl} className="w-full h-full object-cover" />
          </div>
        )}

        {!hasNewFile && !existingUrl && (
          <div className="w-16 h-16 rounded-md border bg-gray-50 flex items-center justify-center text-xs text-gray-400">
            No image
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label className="inline-flex px-3 py-1 bg-white border rounded-md cursor-pointer text-sm">
          Upload
          <input
            type="file"
            accept={field.accept || "image/*"}
            className="hidden"
            onChange={(e) =>
              handleFileChange(field.name, e.target.files, field.readOnly)
            }
          />
        </label>

        {!field.readOnly && (
          <button
            type="button"
            className="text-sm text-red-500 hover:underline"
            onClick={() => {
              previews[field.name]?.forEach((u) => URL.revokeObjectURL(u));
              setPreviews((s) => ({ ...s, [field.name]: [] }));
              setValue(field.name as any, null as any);
            }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};





  const hasEditableInput = fields.some((f) => "name" in f && !(f as InputFieldConfig).readOnly);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-4 p-4 max-w-2xl  grid grid-cols md:grid-cols-2 gap-4 "
    >
      {fields.map((field, idx) => {
        if ("sectionTitle" in field) {
          return (
            <div key={`section-${idx}`} className="md:col-span-2  mb-2 border-b border-blue-300  pb-1">
              <h3 className="text-lg font-semibold text-blue-600">{field.sectionTitle}</h3>
            </div>
          );
        }
        const inputField = field as InputFieldConfig;
        if (inputField.conditional) {
          const conditionValue = watch(inputField.conditional.field as any);
          if (conditionValue !== inputField.conditional.value) {
            return null; // Hide this field
          }
        }

        const currentValue = watch(inputField.name as any);
        const apiError = error && error[inputField.name] ? error[inputField.name].join(", ") : "";
        const validationRules: RegisterOptions = {};

        if (inputField.required)
          validationRules.required = inputField.errorMessage ?? `${inputField.label} is required`;

        if (inputField.pattern)
          validationRules.pattern = {
            value: new RegExp(inputField.pattern),
            message: inputField.errorMessage ?? `${inputField.label} has invalid format`,
          } as any;

        if (inputField.maxLength !== undefined)
          validationRules.maxLength = {
            value: inputField.maxLength,
            message:
              inputField.errorMessage ??
              `${inputField.label} must be at most ${inputField.maxLength} characters`,
          };

        if (inputField.minLength !== undefined)
          validationRules.minLength = {
            value: inputField.minLength,
            message:
              inputField.errorMessage ??
              `${inputField.label} must be at least ${inputField.minLength} characters`,
          };

        if (inputField.name === "password_confirmation") {
          validationRules.validate = (value: string) => {
            const passwordValue = watch("password" as Path<T>);
            return value === passwordValue || "Passwords do not match";
          };
        }

        return (
          <div key={inputField.name}>
            <label className="block text-sm font-medium mb-1 text-gray-700">
              {inputField.label}
              {inputField.required && <span className="text-red-500"> *</span>}
            </label>

            {inputField.type === "textarea" ? (
              <>
                <textarea
                  {...register(inputField.name as any, validationRules as any)}
                  placeholder={inputField.placeholder}
                  readOnly={inputField.readOnly}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-300 "
                />
                <p className="mt-1 text-sm text-red-600">{(errors as any)[inputField.name]?.message}</p>
                <p className="mt-1 text-sm text-red-600">{apiError}</p>
              </>
            ) : inputField.type === "info" ? (
              <>
                <div className="md:col-span-2  ">
                  <p className="text-sm text-gray-600">{inputField.infoText ?? inputField.defaultValue ?? inputField.placeholder ?? inputField.label}</p>
                </div>
              </>
            ) : inputField.type === "chips" ? (
              <>
                <div key={inputField.name} className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {/* {inputField.label} */}
                    {/* {inputField.required && <span className="text-red-500"> *</span>} */}
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {inputField.options?.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleChipClick(inputField.name, option, inputField.readOnly)}
                        className={`px-4 py-2 rounded-full font-medium transition ${currentValue === option
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
          
                <p className="mt-1 text-sm text-red-600">{(errors as any)[inputField.name]?.message}</p>
                <p className="mt-1 text-sm text-red-600">{apiError}</p>
              </>
            ) : inputField.type === "file" ? (
              <>
                <FilePreview field={inputField} />
                <p className="mt-1 text-sm text-red-600">{(errors as any)[inputField.name]?.message}</p>
                <p className="mt-1 text-sm text-red-600">{apiError}</p>
              </>
            ) : (
              (() => {
                const reg = register(inputField.name as any, validationRules as any);
                const combinedOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (inputField.onChange) inputField.onChange(e);
                  if (typeof reg.onChange === "function") reg.onChange(e);
                };
                return (
                  <>
                    <Input
                      type={inputField.type}
                      placeholder={inputField.placeholder}
                      maxLength={inputField.maxLength}
                      readOnly={inputField.readOnly}
                      prefix={inputField.prefix}
                      {...reg}
                      onChange={combinedOnChange}
                    />
                    <p className="mt-1 text-sm text-red-600">{(errors as any)[inputField.name]?.message}</p>
                    <p className="mt-1 text-sm text-red-600">{apiError}</p>
                  </>
                );
              })()
            )}
          </div>
        );
      })}

      <div className="flex gap-3 justify-end mt-6 md:col-span-2">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={!hasEditableInput || isSubmitting}>
          {isSubmitting ? "Saving..." : submitLabel}
        </Button>
      </div>
    </form>
  );
});

export default FormBuilder;
